// Revised 2024 species traits that change rules (not resources): damage resistances.
const lower = (value) => String(value ?? '').trim().toLowerCase();
export const speciesId = (character) => lower(typeof character?.race === 'string' ? character.race : character?.race?.name || character?.race?.Name);

/** Halfling Nimbleness (2024): it can move through the space of any creature a size larger than it, but not stop there. */
export const movesThroughLargerCreatures = (character) => speciesId(character) === 'halfling';
// Dragonborn Draconic Ancestry and Tiefling Fiendish Legacy, by key, for records that kept only the key.
const DRAGON_DAMAGE = Object.freeze({ black: 'acid', blue: 'lightning', brass: 'fire', bronze: 'lightning', copper: 'acid', gold: 'fire',
  green: 'poison', red: 'fire', silver: 'cold', white: 'cold' });
const LEGACY_DAMAGE = Object.freeze({ abyssal: 'poison', chthonic: 'necrotic', infernal: 'fire' });

/**
 * The Dragonborn's Draconic Ancestry damage type ('fire', ...), from the saved choice or its key; '' if unknown.
 * Character creation saves the choice on the species (`race.selectedAncestry`) and, for most records, on the
 * character too; the sheet reads the species first, and so does this.
 */
export const dragonbornAncestryType = (character = {}) => {
  const race = character?.race && typeof character.race === 'object' ? character.race : {};
  const raceKey = lower(race.selectedAncestryKey), key = lower(character.dragonAncestryKey);
  return lower(race.selectedAncestry?.damageType || race.dragonAncestries?.[raceKey]?.damageType || DRAGON_DAMAGE[raceKey]
    || character.dragonAncestry?.damageType || race.dragonAncestries?.[key]?.damageType || DRAGON_DAMAGE[key]);
};

/**
 * Damage types the character's species resists: Dwarven Resilience (Poison), the Dragonborn's ancestry type and the
 * Tiefling's legacy type, read from the choice saved on the character (or the species data for older records).
 */
export const speciesDamageResistances = (character = {}) => {
  const species = speciesId(character);
  const race = character?.race && typeof character.race === 'object' ? character.race : {};
  if (species === 'dwarf') return [...new Set(['poison', ...(race.resistances || []).map(lower)])];
  if (species === 'dragonborn') {
    const type = dragonbornAncestryType(character);
    return type ? [type] : [];
  }
  if (species === 'tiefling') {
    const key = lower(character.tieflingLegacyKey);
    const type = character.tieflingLegacy?.resistance || race.fiendishLegacies?.[key]?.resistance || LEGACY_DAMAGE[key];
    return type ? [lower(type)] : [];
  }
  return (race.resistances || []).map(lower);
};

// Species features that last 10 minutes of game time (100 rounds), kept as combat effects so Spell Time and rests end
// them: Large Form (Goliath 5) and Draconic Flight (Dragonborn 5). Both take a Bonus Action.
export const SPECIES_TIMED_EFFECTS = Object.freeze({
  'goliath-large-form': Object.freeze({ definitionId: 'species:large-form', name: 'Large Form', icon: '⛰️', size: 'Large', speedBonus: 10,
    description: 'Large for 10 minutes (if there is room): Advantage on Strength checks, and your Speed increases by 10 feet.' }),
  'dragonborn-draconic-flight': Object.freeze({ definitionId: 'species:draconic-flight', name: 'Draconic Flight', icon: '🐉',
    description: 'Spectral wings: a Fly Speed equal to your Speed for 10 minutes.' }),
  // Stonecunning (Dwarf): the app has no senses or hidden-creature automation, so the effect is a visible reminder.
  'dwarf-stonecunning': Object.freeze({ definitionId: 'species:tremorsense', name: 'Tremorsense 60 ft', icon: '🪨',
    description: 'Stonecunning: Tremorsense with a range of 60 feet for 10 minutes, while you are on or touching a stone surface.' }),
});
// 2024 Goliath Giant Ancestry, chosen with the species (Proficiency Bonus uses per Long Rest for the chosen option).
export const GIANT_ANCESTRY_ID = 'species:giant-ancestry';
export const GIANT_ANCESTRY_USE = 'goliath-giant-ancestry';
/** The Goliath's Giant Ancestry key (cloud, fire, frost, hill, stone, storm), or '' when none is recorded. */
export const giantAncestryKey = (character = {}) => (speciesId(character) === 'goliath'
  ? lower(character.giantAncestryKey || character.race?.selectedAncestryKey) : '');
/**
 * The options used when the Goliath hits a target with an attack roll and deals damage: Fire's Burn (+1d10 Fire),
 * Frost's Chill (+1d6 Cold and -10 ft Speed until the start of the Goliath's next turn) and Hill's Tumble (a Large or
 * smaller target has the Prone condition).
 */
export const GIANT_ON_HIT = Object.freeze({
  fire: Object.freeze({ label: "Fire's Burn", dice: Object.freeze({ count: 1, sides: 10 }), type: 'fire', text: '+1d10 Fire damage' }),
  frost: Object.freeze({ label: "Frost's Chill", dice: Object.freeze({ count: 1, sides: 6 }), type: 'cold', slow: true,
    text: '+1d6 Cold damage, and the target’s Speed drops by 10 feet until the start of your next turn' }),
  hill: Object.freeze({ label: "Hill's Tumble", prone: true, text: 'the target (Large or smaller) has the Prone condition' }),
});
const PRONE_SIZES = Object.freeze(['tiny', 'small', 'medium', 'large']);
/** Hill's Tumble: Large or smaller (a creature with no recorded size counts as Medium). */
export const tumbleSizeEligible = (size) => PRONE_SIZES.includes(lower(size) || 'medium');
export const SPECIES_BONUS_ACTIONS = Object.freeze(['goliath-large-form', 'dragonborn-draconic-flight', 'orc-adrenaline-rush', 'dwarf-stonecunning']);

/** The combat effect a species use starts, or null for one that has none. */
export const createSpeciesEffect = (featureId, { combatantId, eventId }) => {
  const definition = SPECIES_TIMED_EFFECTS[featureId];
  if (!definition) return null;
  return { id: `${definition.definitionId}:${combatantId}:${eventId}`, definitionId: definition.definitionId, name: definition.name, icon: definition.icon,
    userVisible: true, targetCombatantId: String(combatantId), sourceCombatantId: String(combatantId), gameTime: true,
    stackKey: `${definition.definitionId}:${combatantId}`, stackPolicy: 'replace', expiration: { type: 'elapsedRounds', remainingRounds: 100 },
    duration: '10 minutes', description: definition.description, removeOnSourceRemoval: false,
    ...(definition.size ? { size: definition.size } : {}),
    ...(definition.speedBonus ? { modifiers: [{ type: 'speed', operation: 'add', value: definition.speedBonus, stackingGroup: definition.definitionId }] } : {}) };
};
const activeOn = (combat, combatantId, definitionId) => (Array.isArray(combat) ? combat : combat?.activeEffects || [])
  .some((effect) => effect?.definitionId === definitionId && String(effect.targetCombatantId) === String(combatantId));
/** A size a combat effect gives the creature (Large Form), or null. */
export const effectSize = (combat, combatantId) => (Array.isArray(combat) ? combat : combat?.activeEffects || [])
  .find((effect) => effect?.size && String(effect.targetCombatantId) === String(combatantId))?.size || null;
export const hasLargeForm = (combat, combatantId) => activeOn(combat, combatantId, 'species:large-form');
export const hasDraconicFlight = (combat, combatantId) => activeOn(combat, combatantId, 'species:draconic-flight');
